import React, { ChangeEventHandler, useContext, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import ErrorAlert from '../../common/components/ErrorAlert';
import Button from '../../common/components/button';
import Checkbox from '../../common/components/checkbox';
import DateFields from '../../common/components/DateFields';
import Div from '../../common/components/div';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import StackingButtons from '../../common/components/StackingButtons';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import ValidationAlertMessage from '../../common/components/ValidationAlertMessage';
import { ApiError, getUnlockAccountError, hasInvalidField, isAlreadyUnlockedMessage } from '../../common/functions/getApiErrorMessage';
import { isValidYear } from '../../common/functions/isValidYear';
import translate from '../../common/functions/translate';
import { useConversion } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/useTrait';
import is from '../../common/objects/is';
import { DateObject } from '../../common/objects/models/date.field.value';
import LastNameSection from '../../common/components/LastNameSection';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import useUnlockAccountApi from '../../common/hooks/useUnlockAccountApi';
import { createOrUnlockAccountData } from '../../create-account/createAccountContext';
import { scrollIntoViewAndFocus } from '../../common/functions/scrollIntoViewAndFocus';
import { TextField } from '@cmsgov/ds-medicare-gov';
import { HorizontalRule } from '../../common/components/HorizontalRule';

export type UnlockBasicInformationErrorKeys = 'loginNow' | 'checkInformation' | 'processReenter' | 'inconvenienceTryAgain' | 'inconvenienceTryUnlocking' | "invalidMBI" | "isAlreadyUsed" | "pleaseEnterUsername" | "passwordGuidelines";
export type UnlockBasicInformationErrorHeaderKeys = 'cantFind' | 'completeItems' | 'alreadyExists' | 'cantProcess' | 'alreadyHaveAccount';
type UnlockBasicInformationErrorListKeys = 'invalidLastName' | 'invalidZipOrCity' | 'invalidDOB' | 'invalidEmail';
interface YourMedicareInformationProps {
    testInitialErrorState?: UnlockBasicInformationErrorKeys & UnlockBasicInformationErrorListKeys & UnlockBasicInformationErrorHeaderKeys
    basicInfoHeaders?: UnlockBasicInformationErrorHeaderKeys
}

const YourBasicInformation = (props: YourMedicareInformationProps) => {
    const [certificationCheckedRef, setCertificationCheckedRef] = useState<HTMLElement | null>(null);
    const [confirmEmailAddressRef, setConfirmEmailAddressRef] = useState<HTMLElement | null>(null);
    const [dayRef, setDayRef] = useState<HTMLElement | null>(null);
    const [emailAddressRef, setEmailAddressRef] = useState<HTMLElement | null>(null);
    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [monthRef, setMonthRef] = useState<HTMLElement | null>(null);
    const [yearRef, setYearRef] = useState<HTMLElement | null>(null);
    const [zipCodeRef, setZipCodeRef] = useState<HTMLElement | null>(null);
    const confirmEmailAddressError = useTrait(false);
    const dateFieldKey = useTrait(new Date().getTime());
    const emailAddressError = useTrait(false);
    const lastNameError = useTrait(false);
    const validateEntireForm = useTrait(false);
    const zipCodeOrCityError = useTrait(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAlreadyUnlocked, setIsAlreadyUnlocked] = useState(false);
    const history = useHistory();
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [certificationTouched, setCertificationTouched] = useState(false);

    const api = useUnlockAccountApi();
    const { data, updateData, stepBackward, stepForward } = useContext(createOrUnlockAccountData);
    const {
        suffix,
        lastName,
        dateOfBirth,
        zipCodeOrCity,
        emailAddress,
        noEmailAddressChecked,
        confirmEmailAddress,
        certificationChecked
    } = data;

    const conversion = useConversion('unlocked_account', 'unlocked_account');

    const [errorMessage, setErrorMessage] = useState<JSX.Element | string | null>(null);
    const [errorKey, setErrorKey] = useState<UnlockBasicInformationErrorKeys | null>(props.testInitialErrorState || null);
    const [errorListKey, setErrorListKey] = useState<UnlockBasicInformationErrorListKeys | null>(props.testInitialErrorState || null);
    const [errorKeyHeader, setErrorKeyHeader] = useState<UnlockBasicInformationErrorHeaderKeys | null>(props.basicInfoHeaders || null);
    const [errorAlertHeading, setErrorAlertHeading] = useState("");
    const [apiErrorMessageList, setApiErrorMessageList] = useState<JSX.Element[]>([]);
    const [analyticsErrorKey, setAnalyticsErrorKey] = useState<UnlockBasicInformationErrorKeys | UnlockBasicInformationErrorListKeys | null>(null);

    const currentListKey = useRef<string | null>(null);

    useEffect(() => {
        const errorHeaders: {
            [key in UnlockBasicInformationErrorHeaderKeys]: string;
        } = {
            cantFind: translate("we_cant_find_you"),
            completeItems: translate("complete_items_to_unlock_account"),
            alreadyExists: translate("looks_like_already_have_account"),
            alreadyHaveAccount: translate('looks_like_already_have_account'),
            cantProcess: translate("we_cant_process_request")
        };
        setErrorAlertHeading(
            errorKeyHeader ? errorHeaders[errorKeyHeader] : ""
        );
    }, [setErrorAlertHeading, errorKeyHeader]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in UnlockBasicInformationErrorKeys]: string | JSX.Element
        } = {
            checkInformation: <>
                {translate("check_information_try_again")}{" "}
                {translate("for_more_help_call")}{" "}
                (<a href="tel:18006334227">1-800-633-4227</a>).{" "}
                {translate("tty_users_can_call")}{" "}
                <a href="tel:18774862048">1-877-486-2048</a>.
            </>,
            processReenter: translate("we_cant_process_request_reenter_and_try_again"),
            inconvenienceTryAgain: translate("we_are_sorry_for_inconvenience_try_again"),
            inconvenienceTryUnlocking: translate("we_are_sorry_for_inconvenience_try_unlocking_account_later"),
            loginNow: <>
                <Link to={'/login'}>{translate("log_in_upper")}</Link> {translate("now_start_using_account")}
                <Link to={'/forgot-username-and-password'}>{translate("get_help")}</Link> {translate("do_not_remember_your_username_and_password")}
            </>,
            invalidMBI: translate("please_check_mbi"),
            isAlreadyUsed: translate("you_entered_password_used_before"),
            pleaseEnterUsername: translate("please_enter_username_meet_guidelines"),
            passwordGuidelines: translate("the_password_does_not_meet_required_enter_correct_info"),
        }
        setErrorMessage(errorKey ? errorKeyToMessage[errorKey] : null);
    }, [errorKey, setErrorMessage]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in UnlockBasicInformationErrorListKeys]: JSX.Element
        } = {
            invalidLastName: <>
                <ValidationAlertMessage
                    description={translate("check_last_name")}
                    fieldReference={lastNameRef}
                    linkText={translate("last_name")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            invalidDOB: <>
                <ValidationAlertMessage
                    description={translate("enter_your_dob")}
                    fieldReference={dayRef}
                    linkText={translate("date_of_birth")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            invalidZipOrCity: <>
                <ValidationAlertMessage
                    description={translate("enter_your_zipcode_or_city")}
                    fieldReference={zipCodeRef}
                    linkText={translate("zip_code_or_city")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            invalidEmail: <>
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate("email")}
                    componentHeading={translate('unlock_account')}
                />
            </>
        }

        if (currentListKey.current === errorListKey) {
            return;
        }
        if (errorListKey && errorListKey in errorKeyToMessage) {
            setApiErrorMessageList([errorKeyToMessage[errorListKey], ...apiErrorMessageList]);
        } else {
            setApiErrorMessageList([]);
        }
        currentListKey.current = errorListKey;
    }, [errorListKey, apiErrorMessageList, lastNameRef, dayRef, zipCodeRef, emailAddressRef]);

    useEffect(() => {
        const errorStrings: {
            [key in UnlockBasicInformationErrorKeys | UnlockBasicInformationErrorListKeys]: string;
        } = {
            checkInformation: "We can't find you in our records. Check the information you entered and try again. For more help, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
            processReenter: "We couldn't process your request. Please re-enter your information and try again.",
            inconvenienceTryAgain: "We're sorry for any inconvenience. Please try again.",
            inconvenienceTryUnlocking: "We're sorry for any inconvenience. Try unlocking your account later.",
            loginNow: "Log in now to start using your account. Get help if you don't remember your username or password.",
            invalidMBI: "Please check your Medicare Number.",
            isAlreadyUsed: "You entered a password you've used before.",
            pleaseEnterUsername: "Please enter a username that meets the username guidelines.",
            passwordGuidelines: "The Password does not meet the required guidelines. Please enter the correct information and try again.",
            invalidLastName: "Check your last name.",
            invalidZipOrCity: "Enter your ZIP code or city.",
            invalidEmail: 'Enter your email address or select "I don\'t have an email address."',
            invalidDOB: "Enter your date of birth."
        };
        if (analyticsErrorKey) {
            conversion.failAPIIntermediate(errorStrings[analyticsErrorKey]);
        }
    }, [analyticsErrorKey, conversion]);

    const callUnlockAccount = async () => {
        const lastNameWithSuffix = suffix ? `${lastName} ${suffix}` : lastName;
        const response = await api.step2(lastNameWithSuffix, dateOfBirth, zipCodeOrCity);
        setIsLoading(false);
        const statusCode = response!.status;
        validateEntireForm.set(false);
        dateFieldKey.set(new Date().getTime());

        switch (statusCode) {
            case 204:
                stepForward();
                break;
            case 400:
                const isNotVerified = response?.data?.errors?.some((error: ApiError) => error.message?.includes('not verified'));
                const didNotMatch = response?.data?.errors?.some((error: ApiError) => error.message?.includes("did not match"));
                const invalidLastName = hasInvalidField(response?.data.errors, 'lastName');
                const invalidZipOrCity = hasInvalidField(response?.data.errors, 'zipCodeOrCity');
                if (isNotVerified || didNotMatch) {
                    setErrorKeyHeader('cantFind');
                    setErrorKey('checkInformation');
                    setAnalyticsErrorKey('checkInformation');
                } else if (invalidLastName) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('invalidLastName');
                    setAnalyticsErrorKey('invalidLastName');
                } else if (invalidZipOrCity) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('invalidZipOrCity');
                    setAnalyticsErrorKey('invalidZipOrCity');
                } else {
                    setErrorKeyHeader('cantProcess');
                    setErrorKey('inconvenienceTryAgain');
                    setAnalyticsErrorKey('inconvenienceTryAgain');
                }
                break;
            case 404:
                const notLocked = response?.data?.errors?.some((error: ApiError) => error.message.includes('user is not locked'));
                if (notLocked) {
                    history.push('/login', {showAlreadyUnlockedAlert: true});
                }
                setErrorKeyHeader('cantFind');
                setErrorKey('checkInformation');
                setAnalyticsErrorKey('checkInformation');
                break;
            default:
                setErrorKeyHeader('cantProcess');
                setErrorKey('inconvenienceTryUnlocking');
                setAnalyticsErrorKey('inconvenienceTryUnlocking');
                break;
        }
        const unlockAccountErrorKey = getUnlockAccountError(statusCode, response?.data?.errors);
        setIsAlreadyUnlocked(isAlreadyUnlockedMessage(unlockAccountErrorKey));
        if (errorMessage) {
            setErrorKey(unlockAccountErrorKey);
        }
    };

    const getApiErrorMessage = () => {
        if (!errorMessage)
            return null;
        return errorMessage;
    };

    const getBackButton = () => {
        return <>
            <Button
                onClick={stepBackward}
            >
                {translate('back')}
            </Button>
        </>;
    };

    const getNextButton = () => {
        return <>
            <Button
                variation={'solid'}
                mfaAnalyticsData={{
                    text: translate('next'),
                    parentComponentHeading: translate('create_an_account')
                }}
                type={'submit'}
                isLoading={isLoading}
            >
                {translate('next')}
            </Button>
        </>;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (lastName === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_last_name')}
                    fieldReference={lastNameRef}
                    linkText={translate('last_name')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (dateOfBirth.month === '' || dateOfBirth.day === '' || !isValidYear(dateOfBirth.year))
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_dob')}
                    fieldReference={monthRef}
                    linkText={translate('date_of_birth')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (zipCodeOrCity === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_zipcode_or_city')}
                    fieldReference={zipCodeRef}
                    linkText={translate('zip_code_or_city')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (emailAddress === '' && !noEmailAddressChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate('email_address')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (confirmEmailAddress !== emailAddress && !noEmailAddressChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('confirm_your_email_address')}
                    fieldReference={confirmEmailAddressRef}
                    linkText={translate('confirm_email_address')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (!certificationChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('check_to_certify_your_information')}
                    fieldReference={certificationCheckedRef}
                    linkText={translate('certification_statement')}
                    componentHeading={translate('unlock_account')}
                />
            );
        return messages;
    };

    const handleNextButtonClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);
        resetErrors();
        if (isLoading) {
            return;
        }
        setIsLoading(true);

        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            scrollIntoViewAndFocus(errorFieldRef);
            setIsLoading(false);
            return;
        }
        callUnlockAccount();
    };

    const resetErrors = () => {
        setErrorKeyHeader(null);
        setErrorKey(null);
        setErrorListKey(null);
        lastNameError.set(false);
        zipCodeOrCityError.set(false);
        emailAddressError.set(false);
        emailAddressError.set(false);
        confirmEmailAddressError.set(false);
    };

    const updateCertificationChecked: ChangeEventHandler<HTMLInputElement> = (event) => {
        setCertificationTouched(true);
        if (event.type === 'change') {
            updateData({ certificationChecked: !certificationChecked });
        }
        if (validateEntireForm.get())
            validateForm();
    };

    const updateConfirmEmailAddress = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ confirmEmailAddress: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateConfirmEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateDateOfBirth = (dateObject: DateObject) => {
        updateData({ dateOfBirth: dateObject });
        if (validateEntireForm.get())
            validateForm();
    };

    const updateEmailAddress = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ emailAddress: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateLastName = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ lastName: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateLastName();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateNoEmailAddressChecked: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.type === 'change') {
            if (!noEmailAddressChecked) {
                if (emailAddress !== '')
                    updateData({ emailAddress: '' });
                if (emailAddressError.get() !== null)
                    emailAddressError.set(false);
                if (confirmEmailAddress !== '')
                    updateData({ confirmEmailAddress: '' });
                if (confirmEmailAddressError.get() !== null)
                    confirmEmailAddressError.set(false);
            }
            updateData({ noEmailAddressChecked: !noEmailAddressChecked });
        }
        if (validateEntireForm.get())
            validateForm();
    };

    const updateZipCode = (value: string, doImmediateValidation = false) => {
        updateData({ zipCodeOrCity: value });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateZipCode();
        if (validateEntireForm.get())
            validateForm();
    };

    const validateConfirmEmailAddress = () => {
        const isValid = noEmailAddressChecked || (confirmEmailAddress !== '' && confirmEmailAddress === emailAddress);
        if (confirmEmailAddressError.get() === isValid)
            confirmEmailAddressError.set(!isValid);
        return isValid;
    };

    const validateEmailAddress = () => {
        const isValid = noEmailAddressChecked || emailAddress !== '';
        if (emailAddressError.get() === isValid)
            emailAddressError.set(!isValid);
        return isValid;
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateLastName())
            errorFieldRef = lastNameRef;
        if (dateOfBirth.month === '' && !errorFieldRef)
            errorFieldRef = monthRef;
        if (dateOfBirth.day === '' && !errorFieldRef)
            errorFieldRef = dayRef;
        if (!isValidYear(dateOfBirth.year) && !errorFieldRef)
            errorFieldRef = yearRef;
        if (!validateZipCode() && !errorFieldRef)
            errorFieldRef = zipCodeRef;
        if (!validateEmailAddress() && !errorFieldRef)
            errorFieldRef = emailAddressRef;
        if (!validateConfirmEmailAddress() && !errorFieldRef)
            errorFieldRef = confirmEmailAddressRef;
        if (!certificationChecked && !errorFieldRef)
            errorFieldRef = certificationCheckedRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidationIntermediate(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateLastName = () => {
        const isValid = lastName !== '';
        if (lastNameError.get() === isValid)
            lastNameError.set(!isValid);
        return isValid;
    };

    const validateZipCode = () => {
        const isValid = zipCodeOrCity !== '';
        if (zipCodeOrCityError.get() === isValid)
            zipCodeOrCityError.set(!isValid);
        return isValid;
    };

    const getCertificationError = () => {
        if (!certificationTouched && !submitAttempted) {
            return null;
        }
        return certificationChecked ? null : translate('check_to_certify_your_information');
    }

    return <>
        <form onSubmit={handleNextButtonClick} method="POST">
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow>
                    <Div cols={12}>
                        <legend>
                            <H2 className={'ds-u-margin-top--1 ds-u-margin-bottom--3'}>
                                <TranslatedSpan english={'enter_your_information'}/>
                            </H2>
                        </legend>
                        <AllFieldsRequired />
                        {(getApiErrorMessage() || apiErrorMessageList.length > 0) && getValidationAlertMessages().length === 0 &&
                            <ErrorAlert
                                heading={errorAlertHeading}
                                errorMessage={getApiErrorMessage()}
                                alreadyUnlocked={isAlreadyUnlocked}
                                validationMessages={apiErrorMessageList} />}
                        {getValidationAlertMessages().length !== 0 &&
                            <ErrorAlert
                                headingId={'validationAlertHeader'}
                                heading={translate('complete_items_to_unlock_account')}
                                validationMessages={getValidationAlertMessages()} />}
                    </Div>
                </Div>
                <LastNameSection
                    errorMessage={lastNameError.get() ? translate('enter_your_last_name') : null}
                    inputRef={setLastNameRef}
                    lastNameVal={lastName}
                    onLastNameBlur={updateLastName}
                    onLastNameChange={updateLastName}
                    onSuffixChange={newSuffix => updateData({ suffix: newSuffix })}
                    suffixVal={suffix}
                />
                <HorizontalRule />
                <Div isRow>
                    <Div cols={12}>
                        <DateFields
                            date={dateOfBirth}
                            dayFieldRef={setDayRef}
                            forceValidation={validateEntireForm.get()}
                            hint={translate('use_format_mmddyyyy')}
                            key={dateFieldKey.get()}
                            label={translate('date_of_birth')}
                            monthFieldRef={setMonthRef}
                            onBlur={date => updateDateOfBirth(date)}
                            onChange={date => updateDateOfBirth(date)}
                            yearFieldRef={setYearRef}
                            errorMessage={translate("enter_your_dob")}

                        />
                    </Div>
                </Div>
                <HorizontalRule />
                <Div isRow>
                    <Div
                        cols={12} colsMD={7} colsLG={4} colsXL={3}
                        paddingRight={1}
                    >
                        <TextField
                            errorMessage={zipCodeOrCityError.get() ? translate('enter_your_zipcode_or_city') : null}
                            inputRef={setZipCodeRef}
                            label={translate('zip_code_or_city')}
                            name={'zipCode'}
                            onBlur={event => updateZipCode(event.currentTarget.value.trim(), is.to.be.validated)}
                            onChange={event => updateZipCode(event.currentTarget.value)}
                            value={zipCodeOrCity}
                        />
                    </Div>
                </Div>
                <HorizontalRule />
                <Div isRow>
                    <Div
                        cols={12} colsMD={8} colsLG={6} colsXL={5}
                        paddingRight={1}
                    >
                        <TextField
                            disabled={!!noEmailAddressChecked}
                            errorMessage={emailAddressError.get() ? translate('enter_email_or_select_do_not_have') : null}
                            inputRef={setEmailAddressRef}
                            label={translate('email_address')}
                            name={'emailAddress'}
                            onBlur={event => updateEmailAddress(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateEmailAddress(event.currentTarget.value)}
                            value={emailAddress}
                        />
                    </Div>
                </Div>
                <Div isRow>
                    <Div
                        cols={12} colsMD={8} colsLG={6} colsXL={5}
                        paddingRight={1}
                    >
                        <TextField
                            disabled={!!noEmailAddressChecked}
                            errorMessage={confirmEmailAddressError.get() ? translate('confirm_your_email_address') : null}
                            inputRef={setConfirmEmailAddressRef}
                            label={translate('confirm_email_address')}
                            name={'confirmEmailAddress'}
                            onBlur={event => updateConfirmEmailAddress(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateConfirmEmailAddress(event.currentTarget.value)}
                            value={confirmEmailAddress}
                        />
                    </Div>
                </Div>

                <Div isRow>
                    <Div
                        cols={12}
                        paddingTop={2}
                    >
                        <Checkbox
                            checked={noEmailAddressChecked}
                            label={
                                <>
                                    <span className="ds-u-font-size--base" style={{ textTransform: 'none' }}>
                                        {translate('not_have_an_email_address')}
                                    </span>
                                    <p className="ds-u-margin-top--1 ds-u-margin-bottom--0 ds-u-font-weight--normal ds-u-sans ds-u-font-size--base" style={{ textTransform: 'none' }}>
                                        {translate('we_will_send_info_by_mail')}
                                    </p>
                                </>
                            }
                            className={'ds-u-margin-top--0'}
                            name={'noEmailAddressCheckbox'}
                            onBlur={event => updateNoEmailAddressChecked(event)}
                            onChange={event => updateNoEmailAddressChecked(event)}
                            title={translate('do_not_have_email_address')}
                            type={'checkbox'}
                            value={'noEmailAddress'}
                        >
                        </Checkbox>
                    </Div>
                </Div>
                <HorizontalRule marginBottom={2} />
                <Div isRow>
                    <Div cols={12} colsMD={8} paddingTop={2}>
                        <Checkbox
                            aria-invalid={(!!getCertificationError()).toString()}
                            checked={certificationChecked}
                            label={
                                <H3 style={{ textTransform: 'none' }}>
                                    {translate('agree_to_statement_below')}
                                </H3>
                            }
                            className={'ds-u-margin-top--0 ds-u-measure--wide'}
                            errorMessage={getCertificationError()}
                            inputRef={setCertificationCheckedRef}
                            name={'certificationCheckbox'}
                            onBlur={() => setCertificationTouched(true)}
                            onChange={event => updateCertificationChecked(event)}
                            title={translate('check_box_to_certify_this_information')}
                            type={'checkbox'}
                            value={'certified'}
                        >
                        </Checkbox>
                    </Div>
                </Div>
            </fieldset>
            <StackingButtons
                marginTop={2}
                button1={getBackButton()}
                button2={getNextButton()}
            />
        </form>
    </>;
};

export default YourBasicInformation;
